import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import { initializeTitleAnimation } from '../script/title-animation';
import { ROUTES } from '../../../../routes/Paths';
import { query, where, onSnapshot } from 'firebase/firestore';
import { getClientCollection } from '../../../../../services/FirebaseService';
import '../style/hours-section.css';

interface HorarioData {
    storeHours: {
        monThu: { pt: string; en: string };
        friSat: { pt: string; en: string };
        sundayHolidays: { pt: string; en: string };
    };
    workshopHours: {
        monThu: { pt: string; en: string };
        friSat: { pt: string; en: string };
        sundayHolidays: { pt: string; en: string };
    };
}

const HoursSection: React.FC = () => {
    const { t, language } = useLanguage();
    const [horarioData, setHorarioData] = useState<HorarioData | null>(null);

    // Access translations
    const hoursTranslations = useMemo(() => {
        const hours = (t as any).HOURS || {};
        return {
            tagline: hours.tagline?.[language] || (language === 'pt' ? 'Horário de Funcionamento' : 'Opening Hours'),
            title: hours.title?.[language] || (language === 'pt' 
                ? 'Onde estamos e <span>quando estamos abertos</span>'
                : 'Where we are and <span>when we are open</span>'),
            description: hours.description?.[language] || (language === 'pt'
                ? 'Loja e oficina disponíveis para apoiar o seu veículo sempre que precisar.'
                : 'Store and workshop available to support your vehicle whenever you need.'),
            store: hours.store?.[language] || (language === 'pt' ? 'LOJA' : 'STORE'),
            workshop: hours.workshop?.[language] || (language === 'pt' ? 'OFICINA' : 'WORKSHOP'),
            monThu: hours.monThu?.[language] || (language === 'pt' ? 'Seg - Qui' : 'Mon - Thu'),
            friSat: hours.friSat?.[language] || (language === 'pt' ? 'Sex - Sáb' : 'Fri - Sat'),
            sundayHolidays: hours.sundayHolidays?.[language] || (language === 'pt' ? 'Domingos e Feriados' : 'Sundays & Holidays'),
            contactUs: hours.contactUs?.[language] || (language === 'pt' ? 'Fale Connosco' : 'Contact Us')
        };
    }, [t, language]);

    // Load horario from Firestore with real-time listener
    useEffect(() => {
        const horarioQuery = query(
            getClientCollection('horario'),
            where('type', '==', 'main')
        );

        // Use onSnapshot for real-time updates
        const unsubscribe = onSnapshot(
            horarioQuery,
            (snapshot) => {
                if (snapshot.docs.length > 0) {
                    const raw = snapshot.docs[0].data() as any;
                    const mapHours = (h: any) => ({
                        monThu: h?.monThu || h?.weekdays || { pt: '', en: '' },
                        friSat: h?.friSat || { pt: '', en: '' },
                        sundayHolidays: h?.sundayHolidays || h?.sunday || h?.holidays || { pt: '', en: '' },
                    });
                    const data: HorarioData = {
                        storeHours: mapHours(raw.storeHours),
                        workshopHours: mapHours(raw.workshopHours),
                    };
                    setHorarioData(data);
                } else {
                    setHorarioData(null);
                }
            },
            () => {
                setHorarioData(null);
            }
        );

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, []); // Only set up listener once, it will update automatically

    // Initialize title animation only once on first page load
    useEffect(() => {
        const hasInitialized = sessionStorage.getItem('hours-titles-initialized');
        
        const timer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('hours-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.hours-section .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [language]); // Re-run when language changes

    return (
        <section className="hours-section">
            <div className="hours-section__bg-shape"></div>
            <div className="container">
                <div className="hours-section__content">
                    {/* Section Title */}
                    <div className="section-title text-center sec-title-animation animation-style2">
                        <div className="section-title__tagline-box">
                            <div className="section-title__tagline-border">
                                <div className="section-title__shape-1">
                                    <i className="section-title__circle"></i>
                                </div>
                            </div>
                            <h6 className="section-title__tagline">
                                {hoursTranslations.tagline}
                            </h6>
                            <div className="section-title__tagline-border">
                                <div className="section-title__shape-2">
                                    <i className="section-title__circle"></i>
                                </div>
                            </div>
                        </div>
                        <h2
                            className="section-title__title title-animation"
                            key={`hours-title-${language}`}
                            dangerouslySetInnerHTML={{ __html: hoursTranslations.title }}
                        />
                        <p className="hours-section__description">
                            {hoursTranslations.description}
                        </p>
                        {/* Address as text (A7) */}
                        <p className="hours-section__address">
                            Centro Auto Roady Leiria · Rua da Baquelite 204, Leiria
                        </p>
                    </div>

                    {/* Modern Hours Display */}
                    <div className="hours-section__modern-container wow fadeInUp" data-wow-delay="100ms">
                        <div className="hours-modern-card">
                            {/* Store Section */}
                            <div className="hours-modern-section">
                                <div className="hours-modern-header">
                                    <div className="hours-modern-icon store-icon">
                                        <i className="fas fa-store"></i>
                                    </div>
                                    <h4 className="hours-modern-title">{hoursTranslations.store}</h4>
                                </div>
                                <div className="hours-modern-schedule">
                                    <div className="hours-row">
                                        <span className="hours-day">{hoursTranslations.monThu}</span>
                                        <span className="hours-dots"></span>
                                        <span className="hours-time">
                                            {horarioData?.storeHours?.monThu?.[language] || '8h30 - 19h'}
                                        </span>
                                    </div>
                                    <div className="hours-row">
                                        <span className="hours-day">{hoursTranslations.friSat}</span>
                                        <span className="hours-dots"></span>
                                        <span className="hours-time">
                                            {horarioData?.storeHours?.friSat?.[language] || '8h30 - 19h'}
                                        </span>
                                    </div>
                                    <div className="hours-row">
                                        <span className="hours-day">{hoursTranslations.sundayHolidays}</span>
                                        <span className="hours-dots"></span>
                                        <span className="hours-time">
                                            {horarioData?.storeHours?.sundayHolidays?.[language] || '9h - 13h'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="hours-divider">
                                <div className="hours-divider-line"></div>
                                <div className="hours-divider-icon">
                                    <i className="fas fa-clock"></i>
                                </div>
                                <div className="hours-divider-line"></div>
                            </div>

                            {/* Workshop Section */}
                            <div className="hours-modern-section">
                                <div className="hours-modern-header">
                                    <div className="hours-modern-icon workshop-icon">
                                        <i className="fas fa-wrench"></i>
                                    </div>
                                    <h4 className="hours-modern-title">{hoursTranslations.workshop}</h4>
                                </div>
                                <div className="hours-modern-schedule">
                                    <div className="hours-row">
                                        <span className="hours-day">{hoursTranslations.monThu}</span>
                                        <span className="hours-dots"></span>
                                        <span className="hours-time">
                                            {horarioData?.workshopHours?.monThu?.[language] || '8h30 - 12h30, 14h - 19h'}
                                        </span>
                                    </div>
                                    <div className="hours-row">
                                        <span className="hours-day">{hoursTranslations.friSat}</span>
                                        <span className="hours-dots"></span>
                                        <span className="hours-time">
                                            {horarioData?.workshopHours?.friSat?.[language] || '8h30 - 12h30, 14h - 19h'}
                                        </span>
                                    </div>
                                    <div className="hours-row">
                                        <span className="hours-day">{hoursTranslations.sundayHolidays}</span>
                                        <span className="hours-dots"></span>
                                        <span className="hours-time">
                                            {horarioData?.workshopHours?.sundayHolidays?.[language] || '9h - 13h'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Button - Integrated */}
                        <div className="hours-section__cta-integrated">
                            <Link to={ROUTES.CONTACT} className="hours-cta-btn thm-btn">
                                {hoursTranslations.contactUs}
                                <span><i className="icon-next"></i></span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HoursSection;

